// components/EnhancedWithdrawalRequest.js
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import { useAuth } from "../Authcontext";
import { 
  calculateWithdrawalEligibilityEnhanced, 
  validateWithdrawalRequest,
  getUserSettings
} from "../utils/growthCalculations";

const firestore = getFirestore();

const EnhancedWithdrawalRequestModal = ({ userDetails, deposits, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount: "",
    requestType: "profit_only",
    bankDetails: "",
    reason: ""
  });
  const [eligibility, setEligibility] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const { currentUser } = useAuth();

  // Calculate withdrawal eligibility with enhanced calculations
  useEffect(() => {
    const fetchEligibilityData = async () => {
      if (deposits && deposits.length > 0 && currentUser) {
        try {
          // Get user settings first
          const settings = await getUserSettings(currentUser.uid);
          setUserSettings(settings);
          
          // Calculate eligibility with user-specific rates
          const eligibilityData = await calculateWithdrawalEligibilityEnhanced(deposits, currentUser.uid);
          setEligibility(eligibilityData);
        } catch (error) {
          console.error("Error calculating eligibility:", error);
        }
      }
    };

    fetchEligibilityData();
  }, [deposits, currentUser]);

  // Validate form in real-time
  useEffect(() => {
    if (eligibility && formData.amount) {
      const validation = validateWithdrawalRequest(
        parseFloat(formData.amount) || 0,
        formData.requestType,
        eligibility
      );
      setValidationErrors(validation.errors);
    } else {
      setValidationErrors([]);
    }
  }, [formData.amount, formData.requestType, eligibility]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      const withdrawalAmount = parseFloat(formData.amount);
      
      // Final validation
      const validation = validateWithdrawalRequest(withdrawalAmount, formData.requestType, eligibility);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(" "));
      }

      const withdrawalsCollection = collection(firestore, "Withdrawals");
      
      const newWithdrawal = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: userDetails.fullName,
        amount: withdrawalAmount,
        currency: userDetails.currency,
        requestType: formData.requestType,
        bankDetails: formData.bankDetails,
        reason: formData.reason,
        status: "pending",
        requestedAt: serverTimestamp(),
        
        // Include enhanced eligibility snapshot
        eligibilitySnapshot: {
          totalCapital: eligibility.totalCapital,
          totalCurrentValue: eligibility.totalCurrentValue,
          totalProfit: eligibility.totalProfit,
          availableProfit: eligibility.availableProfit,
          availableCapital: eligibility.availableCapital,
          eligibleDepositsCount: eligibility.eligibleDepositsCount,
          lockedDepositsCount: eligibility.lockedDepositsCount
        },
        
        // Include user settings snapshot
        userSettingsSnapshot: {
          monthlyRate: userSettings?.monthlyRate,
          referralBonus: userSettings?.referralBonus,
          adminFeePercentage: userSettings?.adminFeePercentage,
          adminFeeFixed: userSettings?.adminFeeFixed,
          isUsingGlobalRate: userSettings?.isUsingGlobalRate,
          hasActivePromos: userSettings?.promoRates?.length > 0,
          totalReferrals: userSettings?.totalReferrals
        },
        
        metadata: {
          ipAddress: "N/A",
          userAgent: navigator.userAgent,
          enhancedCalculation: true,
          calculationVersion: "2.0"
        }
      };

      await addDoc(withdrawalsCollection, newWithdrawal);
      onSuccess("Withdrawal request submitted successfully! You will be notified once processed.");
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!eligibility || !userSettings) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white">Calculating withdrawal eligibility with your personalized rates...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white/10 backdrop-blur-md rounded-xl p-6 w-full max-w-3xl border border-white/20 max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">💸 Request Withdrawal</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded hover:bg-white/10"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* User Rate Information */}
        <div className="bg-blue-500/20 border border-blue-500/50 text-blue-300 p-4 rounded-lg mb-4">
          <h4 className="font-semibold mb-2">📊 Your Account Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-blue-200">Monthly Rate:</p>
              <p className="text-blue-100 font-bold">
                {userSettings.monthlyRate}%
                {userSettings.isUsingGlobalRate && <span className="text-xs"> (Global)</span>}
              </p>
            </div>
            {userSettings.referralBonus > 0 && (
              <div>
                <p className="text-blue-200">Referral Bonus:</p>
                <p className="text-green-300 font-bold">+{userSettings.referralBonus}%</p>
              </div>
            )}
            {userSettings.promoRates?.length > 0 && (
              <div>
                <p className="text-blue-200">Active Promos:</p>
                <p className="text-purple-300 font-bold">{userSettings.promoRates.length} active</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Eligibility Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white/10 p-4 rounded-lg">
            <h3 className="text-white font-semibold mb-2">📊 Portfolio Summary</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-300">Total Capital:</span>
                <span className="text-white">{userDetails.currency} {eligibility.totalCapital.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Current Value:</span>
                <span className="text-green-400">{userDetails.currency} {eligibility.totalCurrentValue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Total Profit:</span>
                <span className="text-green-400">{userDetails.currency} {eligibility.totalProfit.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white/10 p-4 rounded-lg">
            <h3 className="text-white font-semibold mb-2">💰 Available for Withdrawal</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-300">Profit Available:</span>
                <span className="text-green-400">{userDetails.currency} {eligibility.availableProfit.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Capital Available:</span>
                <span className="text-blue-400">{userDetails.currency} {eligibility.availableCapital.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span className="text-gray-300">Total Available:</span>
                <span className="text-white">{userDetails.currency} {eligibility.totalAvailableForWithdrawal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Withdrawal Rules Status */}
        <div className="bg-blue-500/20 border border-blue-500/50 text-blue-300 p-4 rounded-lg mb-4">
          <h4 className="font-semibold mb-2">📋 Withdrawal Rules Status</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="flex items-center gap-2">
                {eligibility.canMakeAnyWithdrawal ? "✅" : "⏳"} 
                1-Month Rule: {eligibility.eligibleDepositsCount} deposits eligible
              </p>
              <p className="flex items-center gap-2">
                {eligibility.canWithdrawAllCapital ? "✅" : "⏳"} 
                6-Month Rule: {eligibility.availableCapital > 0 ? "Capital available" : "Capital locked"}
              </p>
            </div>
            <div>
              {eligibility.hasLockedFunds && (
                <p className="text-yellow-300">
                  🔒 {eligibility.lockedDepositsCount} deposits still locked
                </p>
              )}
              {eligibility.daysUntilFullAccess > 0 && (
                <p className="text-yellow-300">
                  ⏰ Full access in {eligibility.daysUntilFullAccess} days
                </p>
              )}
            </div>
          </div>
        </div>

        {!eligibility.canMakeAnyWithdrawal ? (
          <>
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 p-4 rounded-lg text-center">
              <h3 className="font-semibold mb-2">🚫 No Withdrawals Available</h3>
              <p>All deposits must be invested for at least 30 days before any withdrawal is possible.</p>
              {eligibility.earliestWithdrawalDate && (
                <p className="mt-2">
                  Earliest withdrawal date: <strong>{eligibility.earliestWithdrawalDate.toLocaleDateString()}</strong>
                </p>
              )}
            </div>
            
            <div className="flex justify-center pt-6">
              <button
                onClick={onClose}
                className="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Understood, Close
              </button>
            </div>
          </>
        ) : (
          <>
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-300 p-3 rounded mb-4">
                {error}
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="bg-yellow-500/20 border border-yellow-500/50 text-yellow-300 p-3 rounded mb-4">
                <ul className="text-sm space-y-1">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Withdrawal Type
                </label>
                <select
                  value={formData.requestType}
                  onChange={(e) => setFormData({ ...formData, requestType: e.target.value })}
                  className="w-full p-3 rounded bg-black/50 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="profit_only">Profit Only (Available immediately after 1 month)</option>
                  {eligibility.availableCapital > 0 && (
                    <option value="capital">Capital + Profit (Available after 6 months)</option>
                  )}
                  <option value="mixed">Custom Mix (Profit + Capital)</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Amount ({userDetails.currency})
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={
                    formData.requestType === "profit_only" 
                      ? eligibility.availableProfit
                      : eligibility.totalAvailableForWithdrawal
                  }
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full p-3 rounded bg-black/50 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Max: ${
                    formData.requestType === "profit_only" 
                      ? eligibility.availableProfit.toFixed(2)
                      : eligibility.totalAvailableForWithdrawal.toFixed(2)
                  }`}
                />
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, amount: eligibility.availableProfit.toFixed(2) })}
                    className="px-3 py-1 bg-green-500/30 text-green-300 rounded text-xs hover:bg-green-500/50"
                    disabled={eligibility.availableProfit <= 0}
                  >
                    Max Profit
                  </button>
                  {eligibility.availableCapital > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, amount: eligibility.totalAvailableForWithdrawal.toFixed(2) })}
                      className="px-3 py-1 bg-blue-500/30 text-blue-300 rounded text-xs hover:bg-blue-500/50"
                    >
                      Max Available
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Bank Account Details
                </label>
                <textarea
                  value={formData.bankDetails}
                  onChange={(e) => setFormData({ ...formData, bankDetails: e.target.value })}
                  className="w-full p-3 rounded bg-black/50 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Bank Name, Account Number, Account Name, Sort Code"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Reason for Withdrawal (Optional)
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full p-3 rounded bg-black/50 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Emergency, investment goal reached, etc."
                />
              </div>

              <div className="bg-yellow-500/20 border border-yellow-500/50 text-yellow-300 p-3 rounded text-sm">
                <p className="font-semibold">⚠️ Enhanced Withdrawal System:</p>
                <ul className="mt-1 text-xs space-y-1">
                  <li>• Calculations based on your personalized rate: {userSettings.monthlyRate}%</li>
                  {userSettings.referralBonus > 0 && (
                    <li>• Including your referral bonus: +{userSettings.referralBonus}%</li>
                  )}
                  <li>• Minimum 30-day investment period before any withdrawal</li>
                  <li>• Profits withdrawable immediately after 30 days</li>
                  <li>• Capital withdrawable only after 6 months (180 days)</li>
                  <li>• Processing may take 3-5 business days</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || validationErrors.length > 0 || !formData.bankDetails}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Request Withdrawal"}
                </button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default EnhancedWithdrawalRequestModal;